import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const PhotoUploader = ({ onUploadComplete }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [albumName, setAlbumName] = useState("");
  const [location, setLocation] = useState("");
  const [showOnProfile, setShowOnProfile] = useState(false);
  const [isPublic, setIsPublic] = useState(false); // New state for public sharing
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addNewFiles(files);
  };

  const addNewFiles = (newFiles) => {
    // Check if files are images
    const validFiles = newFiles.filter((file) =>
      file.type.startsWith("image/")
    );

    if (validFiles.length < newFiles.length) {
      setError("Only image files are allowed");
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setError(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addNewFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one photo to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("photos", file);
      });

      // Add album details
      if (albumName) formData.append("albumName", albumName);
      if (location) formData.append("location", location);
      formData.append("showOnProfile", showOnProfile.toString());
      formData.append("isPublic", isPublic.toString()); // Add public flag to form data

      const response = await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      // Call the completion handler with the uploaded photos data
      onUploadComplete(response.data.photos);

      // Navigate to tagging flow
      navigate("/tagging");
    } catch (error) {
      console.error("Error uploading photos:", error);
      setError("Failed to upload photos. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Upload Your Photos
        </h2>
        <p className="mt-2 text-gray-600">
          Share your best moments with friends
        </p>
      </div>

      {/* Album and Location Details */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Photo Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="albumName"
            >
              Album Name
            </label>
            <input
              id="albumName"
              type="text"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              placeholder="e.g., Summer Vacation, Birthday Party"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="location"
            >
              Location
            </label>
            <input
              id="location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Miami Beach, Central Park"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
            />
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={showOnProfile}
              onChange={(e) => setShowOnProfile(e.target.checked)}
              className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
            />
            <span className="ml-2 text-gray-700">
              Show on My Profile (Visible on your profile page to everyone)
            </span>
          </label>

          {/* New option for public sharing */}
          <label className="flex items-center mt-3">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
            />
            <span className="ml-2 text-gray-700">
              Create Public Link (Anyone with the link can view without an
              account)
            </span>
          </label>

          {isPublic && (
            <div className="ml-7 p-3 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-gray-700">
                  After uploading, you'll get a link you can share with anyone.
                  Viewers won't need an account to see the photos, but they'll
                  need to sign up to download them.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Drag and drop area */}
      <div className="bg-white rounded-xl shadow-xl p-6 md:p-8">
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${
              dragActive
                ? "border-purple-500 bg-purple-50"
                : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
            }`}
          onClick={() => fileInputRef.current.click()}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="max-w-sm mx-auto">
            <div className="mb-4 rounded-full bg-purple-100 w-20 h-20 flex items-center justify-center mx-auto">
              <svg
                className="h-10 w-10 text-purple-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              Drag photos here
            </h3>
            <p className="text-sm text-gray-500 mb-3">or click to browse</p>
            <p className="text-xs text-gray-400">
              Supports JPG, PNG, GIF up to 10MB
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-md">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Selected files preview */}
        {selectedFiles.length > 0 && (
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Selected Photos ({selectedFiles.length})
              </h3>
              <button
                onClick={() => setSelectedFiles([])}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove All
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {selectedFiles.map((file, index) => (
                <div key={index} className="group relative">
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-100 shadow-sm">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index}`}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-40">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="bg-white rounded-full p-1.5 text-gray-700 hover:text-red-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 truncate">
                    {file.name}
                  </p>
                </div>
              ))}

              {/* Add more photos button */}
              <div
                onClick={() => fileInputRef.current.click()}
                className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition"
              >
                <div className="text-center p-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 mx-auto text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <span className="text-sm text-gray-500 mt-2 block">
                    Add More
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload button and progress */}
        <div className="mt-8">
          {uploading ? (
            <div className="space-y-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-600 to-pink-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading {selectedFiles.length} photos...</span>
                <span>{uploadProgress}% complete</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0}
                className={`px-6 py-3 rounded-full text-white font-medium shadow-md transition-all
                  ${
                    selectedFiles.length > 0
                      ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
              >
                {selectedFiles.length > 0
                  ? `Upload ${selectedFiles.length} Photo${
                      selectedFiles.length !== 1 ? "s" : ""
                    }`
                  : "Select Photos First"}
              </button>

              <span className="text-sm text-gray-500">
                After uploading, you'll be able to tag people in your photos
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tips section */}
      <div className="mt-10 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6">
        <h3 className="font-medium text-lg text-gray-900 mb-4">
          Tips for sharing photos
        </h3>
        <ul className="space-y-2">
          <li className="flex gap-3 items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center">
              <span className="text-purple-700 text-sm font-medium">1</span>
            </div>
            <span className="text-gray-700">
              Check "Show on Profile" to share photos with everyone who views
              your profile
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-pink-200 flex items-center justify-center">
              <span className="text-pink-700 text-sm font-medium">2</span>
            </div>
            <span className="text-gray-700">
              Create a public link to share your album with anyone, even
              non-users
            </span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center">
              <span className="text-purple-700 text-sm font-medium">3</span>
            </div>
            <span className="text-gray-700">
              People can only download photos where they're tagged or if they're
              the uploader
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PhotoUploader;
